import { Injectable } from '@angular/core';
import { OrderFieldConfigMap } from 'src/app/module/order/interface/order-field.interface';
import { BehaviorSubject } from 'rxjs';
import { PropertyFieldConfigMap } from 'src/app/module/property/interface/property.interface';
import { AppEnumMap, EnumItem } from '../interface/common.interface';
import { CompanySetting } from '../interface/company.interface';

@Injectable({
  providedIn: 'root',
})
export class CompanyState {
  constructor() {}

  /** Stores details related to the current company */
  private _companySettingSub$ = new BehaviorSubject<CompanySetting>({} as CompanySetting);
  companySetting$ = this._companySettingSub$.asObservable();

  get companySetting(): CompanySetting {
    return this._companySettingSub$.value;
  }

  set companySetting(company: CompanySetting) {
    this._companySettingSub$.next(company);
  }

  /**
   * Stores details related to all companies for which Profet Valuation is enabled.
   * Used to show company filter for Profet Admin
   */
  private _companyListSub$ = new BehaviorSubject<EnumItem[] | null>(null);
  companyList$ = this._companyListSub$.asObservable();

  get companyList(): EnumItem[] | null {
    return this._companyListSub$.value;
  }

  set companyList(companyList: EnumItem[] | null) {
    this._companyListSub$.next(companyList);
  }

  /**
   * Stores the list of enabled report keys for the current company
   */
  private _enabledReportListSub$ = new BehaviorSubject<string[]>([]);
  enabledReportList$ = this._enabledReportListSub$.asObservable();

  get enabledReportList(): string[] {
    return this._enabledReportListSub$.value;
  }

  set enabledReportList(enabledReportList: string[]) {
    this._enabledReportListSub$.next(enabledReportList);
  }

  /**
   * Store the propertyField configuration for each reportType.
   */
  private _propertyFieldConfigCache = new Map<string, PropertyFieldConfigMap>();

  getPropertyFieldConfig(reportType?: string): PropertyFieldConfigMap {
    return (
      (reportType ? this._propertyFieldConfigCache.get(reportType) : undefined) ??
      this._propertyFieldConfigCache.get('DEFAULT') ??
      ({} as PropertyFieldConfigMap)
    );
  }

  setPropertyFieldConfig(reportType: string, config: PropertyFieldConfigMap): void {
    this._propertyFieldConfigCache.set(reportType, config);
  }

  hasPropertyFieldConfig(reportType: string): boolean {
    return this._propertyFieldConfigCache.has(reportType);
  }

  /**
   * Store the orderField configuration for each reportType.
   */
  private _orderFieldConfigCache = new Map<string, OrderFieldConfigMap>();

  getOrderFieldConfig(reportType?: string): OrderFieldConfigMap {
    return (
      (reportType ? this._orderFieldConfigCache.get(reportType) : undefined) ??
      this._orderFieldConfigCache.get('DEFAULT') ??
      {}
    );
  }

  setOrderFieldConfig(reportType: string, config: OrderFieldConfigMap): void {
    this._orderFieldConfigCache.set(reportType, config);
  }

  /**
   * Store the enumerations configuration for each reportType and default.
   */
  private _enumConfigCache = new Map<string, AppEnumMap>();

  getEnumConfig(reportType?: string): AppEnumMap {
    return (
      (reportType ? this._enumConfigCache.get(reportType) : undefined) ??
      this._enumConfigCache.get('DEFAULT') ??
      ({} as AppEnumMap)
    );
  }

  setEnumConfig(reportType: string, config: AppEnumMap): void {
    this._enumConfigCache.set(reportType, config);
  }

  hasEnumConfig(reportType: string): boolean {
    return this._enumConfigCache.has(reportType);
  }
}
